import React, { useState } from 'react';
import './StarRating.css';

/**
 * StarRating - A beautiful star rating component
 * @param {number} value - Current rating value (1-5)
 * @param {function} onChange - Callback when rating changes
 * @param {string} label - Label text to display above the stars
 * @param {boolean} disabled - Whether the rating is disabled
 * @param {number} size - Size of stars in pixels (default: 32)
 */
export default function StarRating({
  value = 0,
  onChange,
  label = '',
  disabled = false,
  size = 32
}) {
  const [hoveredRating, setHoveredRating] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const maxStars = 5;
  const displayValue = isHovering && hoveredRating > 0 ? hoveredRating : Math.round(value);

  const handleStarClick = (rating) => {
    if (disabled) return;
    onChange(rating);
  };

  const handleStarHover = (rating) => {
    if (disabled) return;
    setHoveredRating(rating);
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    if (disabled) return;
    setIsHovering(false);
    setHoveredRating(0);
  };

  const renderStar = (index) => {
    const starValue = index + 1;
    const isFullStar = displayValue >= starValue;

    const handleClick = () => {
      if (disabled) return;
      handleStarClick(starValue);
    };

    const handleTouchStart = () => {
      if (disabled) return;
      handleStarHover(starValue);
    };

    const handleTouchEnd = () => {
      if (disabled) return;
      handleStarClick(starValue);
      setTimeout(() => {
        setIsHovering(false);
        setHoveredRating(0);
      }, 100);
    };

    const handleHover = () => {
      if (disabled) return;
      handleStarHover(starValue);
    };

    return (
      <span
        key={index}
        className={`star-rating-star ${isFullStar ? 'filled' : 'empty'}`}
        onClick={handleClick}
        onMouseEnter={handleHover}
        onMouseMove={handleHover}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{
          fontSize: `${size}px`,
          cursor: disabled ? 'default' : 'pointer',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          msUserSelect: 'none'
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={`Rate ${starValue} out of ${maxStars} stars`}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleStarClick(starValue);
          }
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
            fill={isFullStar ? '#fbbf24' : '#d1d5db'}
            stroke={isFullStar ? '#f59e0b' : '#9ca3af'}
            strokeWidth="1"
          />
        </svg>
      </span>
    );
  };

  return (
    <div className={`star-rating-container ${disabled ? 'disabled' : ''}`}>
      {label && (
        <label className="star-rating-label">
          {label}
          {displayValue > 0 && (
            <span className="star-rating-value">
              {' '}({displayValue})
            </span>
          )}
        </label>
      )}
      <div
        className="star-rating-stars"
        onMouseLeave={handleMouseLeave}
        role="radiogroup"
        aria-label={`Rating: ${displayValue} out of ${maxStars} stars`}
      >
        {Array.from({ length: maxStars }, (_, i) => renderStar(i))}
      </div>
    </div>
  );
}

